import CompanyService from '@/services/CompanyService'
import IntegrationService from '@/services/IntegrationService'
import LocationService from '@/services/LocationService'
import PromoService from '@/services/PromoService'
import UserService from '@/services/UserService'

export default {
  namespaced: true,
  state: {
    activeCompanyIds: [],
    activeUserId: null,
    activeRouteName: null,
    oldCompanyIds: [],
    oldUserId: null,
    oldRouteName: null,
    campaigns: [],
    companies: [],
    integrations: [],
    locations: [],
    promos: [],
    users: []
  },
  getters: {
    selectRawActiveCompanyIds: (state) => state.activeCompanyIds,
    selectActiveCompanyIds: (state) =>
      state.activeCompanyIds.length
        ? state.activeCompanyIds[0] === 'ALL_COMPANIES'
          ? state.companies.map((company) => company._id)
          : state.activeCompanyIds
        : state.activeCompanyIds,
    selectActiveCompanies: (state) =>
      state.companies.filter(
        (company) =>
          state.activeCompanyIds.includes('ALL_COMPANIES') ||
          state.activeCompanyIds.includes(company._id)
      ),
    selectActiveUserId: (state) => state.activeUserId,
    selectActiveRouteName: (state) => state.activeRouteName,
    selectOldCompanyIds: (state) => state.oldCompanyIds,
    selectOldUserId: (state) => state.oldUserId,
    selectOldRouteName: (state) => state.oldRouteName,
    selectCampaigns: (state) => state.campaigns,
    selectCampaignById: (state) => (campaignId) => {
      return state.campaigns.find((campaign) => campaignId == campaign._id)
    },
    selectCompanies: (state) => state.companies,
    selectCompanyById: (state) => (companyId) => {
      return state.companies.find((company) => companyId == company._id)
    },
    selectIntegrations: (state) => state.integrations,
    selectIntegrationById: (state) => (integrationId) => {
      return state.integrations.find((integration) => integrationId == integration._id)
    },
    selectLocations: (state) => state.locations,
    selectLocationById: (state) => (locationId) => {
      return state.locations.find((location) => locationId == location._id)
    },
    selectActiveLocations: (state) =>
      state.locations.filter(
        (location) =>
          state.activeCompanyIds.includes('ALL_COMPANIES') ||
          state.activeCompanyIds.includes(location.company)
      ),
    selectPromos: (state) => state.promos,
    selectPromoById: (state) => (promoId) => {
      return state.promos.find((promo) => promoId == promo._id)
    },
    selectUsers: (state) => state.users,
    selectUserById: (state) => (userId) => {
      return state.users.find((user) => userId == user._id)
    }
  },
  mutations: {
    SET_ACTIVE_COMPANY_IDS(state, activeCompanyIds) {
      state.activeCompanyIds = activeCompanyIds
    },
    SET_ACTIVE_USER_ID(state, activeUserId) {
      state.activeUserId = activeUserId
    },
    SET_ACTIVE_ROUTE_NAME(state, activeRouteName) {
      state.activeRouteName = activeRouteName
    },
    SET_OLD_COMPANY_IDS(state, oldCompanyIds) {
      state.oldCompanyIds = oldCompanyIds
    },
    SET_OLD_USER_ID(state, oldUserId) {
      state.oldUserId = oldUserId
    },
    SET_OLD_ROUTE_NAME(state, oldRouteName) {
      state.oldRouteName = oldRouteName
    },
    SET_CAMPAIGNS(state, campaigns) {
      state.campaigns = [...campaigns]
    },
    SET_COMPANIES(state, companies) {
      state.companies = [...companies]
    },
    SET_INTEGRATIONS(state, integrations) {
      state.integrations = [...integrations]
    },
    SET_LOCATIONS(state, locations) {
      state.locations = [...locations]
    },
    SET_PROMOS(state, promos) {
      state.promos = [...promos]
    },
    SET_USERS(state, users) {
      state.users = [...users]
    }
  },
  actions: {
    async init({ dispatch }) {
      await Promise.all([
        dispatch('fetchCompanies'),
        dispatch('fetchIntegrations'),
        dispatch('fetchLocations'),
        dispatch('fetchPromos'),
        dispatch('fetchUsers')
      ])
    },
    async setActiveCompanyIds({ commit, dispatch, getters, rootGetters }, activeCompany) {
      let activeCompanyIds = activeCompany ? [activeCompany._id] : []
      const { selectActiveCompanyIds } = getters
      commit('SET_OLD_COMPANY_IDS', selectActiveCompanyIds)
      commit('SET_ACTIVE_COMPANY_IDS', activeCompanyIds)

      let companyIds = activeCompany
        ? activeCompany._id == 'ALL_COMPANIES'
          ? rootGetters['company/companies'].map((company) => company._id)
          : [activeCompany._id]
        : []
      dispatch('featureFlag/setFeatureFlagCompanies', companyIds, { root: true })

      if (activeCompanyIds.length) {
        dispatch('routeInit')
      } else {
        dispatch('routeDestroy')
      }
    },
    async setActiveUserId({ commit, dispatch, getters }, user) {
      let activeUser = user ? user._id : null
      commit('SET_OLD_USER_ID', getters.selectActiveUserId)
      commit('SET_ACTIVE_USER_ID', activeUser)
      if (activeUser) {
        dispatch('routeInit')
      } else {
        dispatch('routeDestroy')
      }
    },
    async setActiveRoute({ commit, dispatch, getters }, route) {
      let routeName = route ? route.name : null
      commit('SET_OLD_ROUTE_NAME', getters.selectActiveRouteName)
      commit('SET_ACTIVE_ROUTE_NAME', routeName)
      if (routeName) {
        dispatch('routeInit')
      } else {
        dispatch('routeDestroy')
      }
    },
    async routeInit({ dispatch, getters }) {
      const { selectRawActiveCompanyIds, selectActiveRouteName, selectActiveUserId } = getters

      if (!selectRawActiveCompanyIds.length || !selectActiveRouteName || !selectActiveUserId) {
        return
      }

      switch (selectActiveRouteName) {
        case 'winback':
          dispatch('winback/winbackInit', null, { root: true })
          return
      }
    },
    async routeDestroy({ dispatch, getters, rootGetters }) {
      const { selectOldRouteName } = getters
      switch (selectOldRouteName) {
        case 'winback':
          dispatch('winback/resetAll', null, { root: true })
          return
      }
    },
    async fetchCompanies({ rootGetters, commit }) {
      const response = await CompanyService.fetchCompanies()
      if (response.body.success !== true) {
        throw response.body
      }
      commit('SET_COMPANIES', response.body.data.companies)
    },

    async fetchIntegrations({ rootGetters, commit }) {
      const onlineUser = rootGetters['user/onlineUser']

      let response = await IntegrationService.listIntegrations({
        companyIds: onlineUser.companies,
        locationIds: onlineUser.role !== 'admin' ? onlineUser.locations : undefined
      })

      if (response.body.success !== true) {
        throw response.body
      }

      commit('SET_INTEGRATIONS', response.body.data.integrations)
    },
    async fetchLocations({ rootGetters, commit }) {
      const onlineUser = rootGetters['user/onlineUser']

      const response = await LocationService.getLocations({
        filters: { companyIds: onlineUser.companies },
        select: 'bandwidth city company friendlyName name'
      })

      if (response.body.success !== true) {
        throw response.body
      }

      commit('SET_LOCATIONS', response.body.data)
    },
    async fetchPromos({ rootGetters, commit }) {
      const onlineUser = rootGetters['user/onlineUser']

      const response = await PromoService.listPromos({
        filters: {
          companyIds: onlineUser.companies
        },
        select: 'hasUniqueInStoreCodes hasUniqueOffPremiseCodes title'
      })

      if (response.body.success !== true) {
        throw response.body
      }

      commit('SET_PROMOS', response.body.data.promos)
    },
    async fetchUsers({ commit }) {
      let response = await UserService.listUsers()

      if (response.body.success !== true) {
        throw response.body
      }
      const users = Object.keys(response.body.data.userMap).map((key) => {
        return {
          ...response.body.data.userMap[key]
        }
      })

      commit('SET_USERS', users)
    }
  }
}
